/**
 * Title + year matching between TMDB's lists and the provider's catalog, for
 * the catalog entries that carry no TMDB id.
 *
 * Home's rails match by the provider's `tmdbId` first. Some panels leave it out
 * on exactly the titles Home features: seen on a 44k-movie catalog where 15.8k
 * movies had ids but none of the week's trending or popular 2026 releases did,
 * although "Spider-Man: Brand New Day - 2026" and "The Odyssey (2026)" were
 * there. Matching by id alone left Trending and Popular Movies empty.
 *
 * Deliberately strict, because a wrong match plays the wrong film:
 * - only entries WITHOUT a tmdbId take part (one with a different id is a
 *   different title of the same name);
 * - the normalized title must be equal, not merely similar;
 * - a movie needs its year to agree with TMDB's release year (within one, for
 *   festival premieres a year before the wide release). "Moana - 2016" is not
 *   the 2026 "Moana";
 * - a series may match on title alone when the catalog gives no year, since
 *   series names are rarely reused and panels often omit the year.
 */

export interface CatalogEntry {
  rawTitle: string;
  tmdbId?: number;
}

export interface TmdbListEntry {
  id: number;
  title: string;
  release_date?: string | null;
}

interface ParsedTitle {
  key: string;
  year?: number;
  /** A camera recording, kept only when nothing better has the title. */
  lowQuality: boolean;
}

export const LOW_QUALITY = /\b(cam|camrip|hdcam|ts|telesync)\b/i;

export function parseCatalogTitle(rawTitle: string): ParsedTitle {
  const lowQuality = LOW_QUALITY.test(rawTitle);
  let title = rawTitle.replace(/\[[^\]]*\]/g, " ");
  let year: number | undefined;
  // "Title - 2026", "Title (2026)", "Title 2026", with tags after the year.
  // Greedy, so the LAST year wins: "Blade Runner 2049 - 2017" is from 2017.
  const match = title.match(/^(.*)[\s\-–(]+((?:19|20)\d{2})\)?(?:\s.*)?$/);
  if (match && match[1]!.trim()) {
    title = match[1]!;
    year = Number(match[2]);
  }
  return { key: normalizeTitle(title), ...(year !== undefined ? { year } : {}), lowQuality };
}

export function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "");
}

export interface TitleIndex<T> {
  byKey: Map<string, Array<{ item: T; year?: number; lowQuality: boolean }>>;
}

/** Index the catalog entries that have no TMDB id, by normalized title. */
export function buildTitleIndex<T extends CatalogEntry>(catalog: readonly T[]): TitleIndex<T> {
  const byKey = new Map<string, Array<{ item: T; year?: number; lowQuality: boolean }>>();
  for (const item of catalog) {
    if (item.tmdbId && item.tmdbId > 0) continue;
    const parsed = parseCatalogTitle(item.rawTitle);
    if (!parsed.key) continue;
    const list = byKey.get(parsed.key) ?? [];
    list.push({ item, ...(parsed.year !== undefined ? { year: parsed.year } : {}), lowQuality: parsed.lowQuality });
    byKey.set(parsed.key, list);
  }
  return { byKey };
}

/**
 * The catalog entry for each TMDB title: by id when the provider supplied it,
 * otherwise by title and year. Titles with no confident match are left out.
 */
export function matchTmdbTitles<T extends CatalogEntry>(
  titles: readonly TmdbListEntry[],
  byId: ReadonlyMap<number, T>,
  index: TitleIndex<T>,
  media: "movie" | "tv",
): Map<number, T> {
  const matches = new Map<number, T>();
  for (const title of titles) {
    if (matches.has(title.id)) continue;
    const direct = byId.get(title.id);
    if (direct) {
      matches.set(title.id, direct);
      continue;
    }
    const candidates = index.byKey.get(normalizeTitle(title.title));
    if (!candidates) continue;
    const tmdbYear = Number(title.release_date?.slice(0, 4)) || undefined;
    const fits = candidates.filter((c) => {
      if (c.year !== undefined && tmdbYear !== undefined) return Math.abs(c.year - tmdbYear) <= 1;
      return media === "tv" && c.year === undefined;
    });
    const best = fits.find((c) => !c.lowQuality) ?? fits[0];
    if (best) matches.set(title.id, best.item);
  }
  return matches;
}
